(function (window, document) {
    "use strict";

    const KSTELO = {
        VERSION: "0.2.0",

        STORAGE_KEYS: {
            VISITOR_ID: "kstelo_visitor_id",
            SESSION_ID: "kstelo_session_id",
            FIRST_VISIT_AT: "kstelo_first_visit_at",
            LAST_VISIT_AT: "kstelo_last_visit_at",
            SESSION_COUNT: "kstelo_session_count",
            SESSION_STARTED_AT: "kstelo_session_started_at",

            ATTRIBUTION_ID: "kstelo_attribution_id",
            UTM_SOURCE: "kstelo_utm_source",
            UTM_MEDIUM: "kstelo_utm_medium",
            UTM_CAMPAIGN: "kstelo_utm_campaign",
            UTM_CONTENT: "kstelo_utm_content",
            UTM_TERM: "kstelo_utm_term",
            FBCLID: "kstelo_fbclid",
            CAMPAIGN_ID: "kstelo_campaign_id",
            ADSET_ID: "kstelo_adset_id",
            AD_ID: "kstelo_ad_id",
            LANDING_PAGE: "kstelo_landing_page",
            REFERRER: "kstelo_referrer"
        },

        SESSION_TIMEOUT_MS: 30 * 60 * 1000,

        EASELL_FIELDS: {
            visitorId: "kstelo_visit_id",
            sessionId: "kstelo_session_id",
            firstVisitAt: "kstelo_first_visit_at",
            lastVisitAt: "kstelo_last_visit_at",
            sessionCount: "kstelo_session_count",

            attributionId: "kstelo_attribution_id",
            utmSource: "kstelo_utm_source",
            utmMedium: "kstelo_utm_medium",
            utmCampaign: "kstelo_utm_campaign",
            utmContent: "kstelo_utm_content",
            utmTerm: "kstelo_utm_term",
            fbclid: "kstelo_fbclid",
            campaignId: "kstelo_campaign_id",
            adsetId: "kstelo_adset_id",
            adId: "kstelo_ad_id",
            landingPage: "kstelo_landing_page",
            referrer: "kstelo_referrer"
        }
    };

    const Utils = {
        nowISO() {
            return new Date().toISOString();
        },

        nowTimestamp() {
            return Date.now();
        },

        generateId(prefix) {
            const randomPart =
                window.crypto &&
                typeof window.crypto.randomUUID === "function"
                    ? window.crypto.randomUUID()
                    : [
                          Date.now().toString(36),
                          Math.random().toString(36).slice(2),
                          Math.random().toString(36).slice(2)
                      ].join("-");

            return `${prefix}_${randomPart}`;
        },

        toInteger(value, fallback = 0) {
            const parsed = Number.parseInt(value, 10);
            return Number.isNaN(parsed) ? fallback : parsed;
        },

        normalize(value) {
            if (value === null || value === undefined) {
                return null;
            }

            const normalized = String(value).trim();
            return normalized === "" ? null : normalized;
        },

        dispatchInputEvents(element) {
            element.dispatchEvent(new Event("input", { bubbles: true }));
            element.dispatchEvent(new Event("change", { bubbles: true }));
        },

        getQueryParam(name) {
            try {
                return this.normalize(
                    new URLSearchParams(window.location.search).get(name)
                );
            } catch (error) {
                console.warn(
                    `[Kstelo] No fue posible leer el parámetro ${name}.`,
                    error
                );
                return null;
            }
        },

        getCurrentUrl() {
            return `${window.location.pathname}${window.location.search}`;
        },

        getExternalReferrer() {
            const referrer = this.normalize(document.referrer);

            if (!referrer) {
                return null;
            }

            try {
                const referrerUrl = new URL(referrer);
                const currentUrl = new URL(window.location.href);

                if (referrerUrl.hostname === currentUrl.hostname) {
                    return null;
                }
            } catch (error) {
                return referrer;
            }

            return referrer;
        }
    };

    const Storage = {
        isAvailable() {
            try {
                const testKey = "__kstelo_storage_test__";
                window.localStorage.setItem(testKey, "1");
                window.localStorage.removeItem(testKey);
                return true;
            } catch (error) {
                return false;
            }
        },

        get(key) {
            if (!this.isAvailable()) {
                return null;
            }

            try {
                return Utils.normalize(window.localStorage.getItem(key));
            } catch (error) {
                console.warn("[Kstelo] No fue posible leer localStorage.", error);
                return null;
            }
        },

        set(key, value) {
            const normalizedValue = Utils.normalize(value);

            if (!this.isAvailable() || normalizedValue === null) {
                return false;
            }

            try {
                window.localStorage.setItem(key, normalizedValue);
                return true;
            } catch (error) {
                console.warn("[Kstelo] No fue posible escribir localStorage.", error);
                return false;
            }
        },

        remove(key) {
            if (!this.isAvailable()) {
                return false;
            }

            try {
                window.localStorage.removeItem(key);
                return true;
            } catch (error) {
                console.warn("[Kstelo] No fue posible eliminar localStorage.", error);
                return false;
            }
        }
    };

    const Attribution = {
        data: null,

        readIncomingData() {
            return {
                utmSource: Utils.getQueryParam("utm_source"),
                utmMedium: Utils.getQueryParam("utm_medium"),
                utmCampaign: Utils.getQueryParam("utm_campaign"),
                utmContent: Utils.getQueryParam("utm_content"),
                utmTerm: Utils.getQueryParam("utm_term"),
                fbclid: Utils.getQueryParam("fbclid"),
                campaignId:
                    Utils.getQueryParam("campaign_id") ||
                    Utils.getQueryParam("campaignid"),
                adsetId:
                    Utils.getQueryParam("adset_id") ||
                    Utils.getQueryParam("adsetid"),
                adId:
                    Utils.getQueryParam("ad_id") ||
                    Utils.getQueryParam("adid"),
                landingPage: Utils.getCurrentUrl(),
                referrer: Utils.getExternalReferrer()
            };
        },

        hasAttributionSignal(data) {
            return Boolean(
                data.utmSource ||
                data.utmMedium ||
                data.utmCampaign ||
                data.utmContent ||
                data.utmTerm ||
                data.fbclid ||
                data.campaignId ||
                data.adsetId ||
                data.adId
            );
        },

        persistIncomingData(data) {
            const hasSignal = this.hasAttributionSignal(data);

            /*
             * Se conserva el último touch publicitario conocido.
             * Una navegación interna o una visita directa posterior no borra
             * la atribución ya capturada.
             */
            if (hasSignal) {
                Storage.set(
                    KSTELO.STORAGE_KEYS.ATTRIBUTION_ID,
                    Utils.generateId("att")
                );

                Storage.set(KSTELO.STORAGE_KEYS.UTM_SOURCE, data.utmSource);
                Storage.set(KSTELO.STORAGE_KEYS.UTM_MEDIUM, data.utmMedium);
                Storage.set(KSTELO.STORAGE_KEYS.UTM_CAMPAIGN, data.utmCampaign);
                Storage.set(KSTELO.STORAGE_KEYS.UTM_CONTENT, data.utmContent);
                Storage.set(KSTELO.STORAGE_KEYS.UTM_TERM, data.utmTerm);
                Storage.set(KSTELO.STORAGE_KEYS.FBCLID, data.fbclid);
                Storage.set(KSTELO.STORAGE_KEYS.CAMPAIGN_ID, data.campaignId);
                Storage.set(KSTELO.STORAGE_KEYS.ADSET_ID, data.adsetId);
                Storage.set(KSTELO.STORAGE_KEYS.AD_ID, data.adId);
                Storage.set(
                    KSTELO.STORAGE_KEYS.LANDING_PAGE,
                    data.landingPage
                );

                if (data.referrer) {
                    Storage.set(
                        KSTELO.STORAGE_KEYS.REFERRER,
                        data.referrer
                    );
                }

                return;
            }

            /*
             * Para una primera visita sin parámetros se conserva igualmente
             * la landing y el referrer, sin inventar una atribución pagada.
             */
            if (!Storage.get(KSTELO.STORAGE_KEYS.LANDING_PAGE)) {
                Storage.set(
                    KSTELO.STORAGE_KEYS.LANDING_PAGE,
                    data.landingPage
                );
            }

            if (
                data.referrer &&
                !Storage.get(KSTELO.STORAGE_KEYS.REFERRER)
            ) {
                Storage.set(
                    KSTELO.STORAGE_KEYS.REFERRER,
                    data.referrer
                );
            }
        },

        loadStoredData() {
            return {
                attributionId: Storage.get(
                    KSTELO.STORAGE_KEYS.ATTRIBUTION_ID
                ),
                utmSource: Storage.get(KSTELO.STORAGE_KEYS.UTM_SOURCE),
                utmMedium: Storage.get(KSTELO.STORAGE_KEYS.UTM_MEDIUM),
                utmCampaign: Storage.get(
                    KSTELO.STORAGE_KEYS.UTM_CAMPAIGN
                ),
                utmContent: Storage.get(KSTELO.STORAGE_KEYS.UTM_CONTENT),
                utmTerm: Storage.get(KSTELO.STORAGE_KEYS.UTM_TERM),
                fbclid: Storage.get(KSTELO.STORAGE_KEYS.FBCLID),
                campaignId: Storage.get(
                    KSTELO.STORAGE_KEYS.CAMPAIGN_ID
                ),
                adsetId: Storage.get(KSTELO.STORAGE_KEYS.ADSET_ID),
                adId: Storage.get(KSTELO.STORAGE_KEYS.AD_ID),
                landingPage: Storage.get(
                    KSTELO.STORAGE_KEYS.LANDING_PAGE
                ),
                referrer: Storage.get(KSTELO.STORAGE_KEYS.REFERRER)
            };
        },

        initialize() {
            const incomingData = this.readIncomingData();
            this.persistIncomingData(incomingData);
            this.data = this.loadStoredData();
            return this.data;
        }
    };

    const Session = {
        data: null,

        getVisitorId() {
            let visitorId = Storage.get(KSTELO.STORAGE_KEYS.VISITOR_ID);

            if (!visitorId) {
                visitorId = Utils.generateId("vis");
                Storage.set(KSTELO.STORAGE_KEYS.VISITOR_ID, visitorId);
            }

            return visitorId;
        },

        getFirstVisitAt() {
            let firstVisitAt = Storage.get(
                KSTELO.STORAGE_KEYS.FIRST_VISIT_AT
            );

            if (!firstVisitAt) {
                firstVisitAt = Utils.nowISO();
                Storage.set(
                    KSTELO.STORAGE_KEYS.FIRST_VISIT_AT,
                    firstVisitAt
                );
            }

            return firstVisitAt;
        },

        isSessionExpired() {
            const lastVisitAt = Storage.get(
                KSTELO.STORAGE_KEYS.LAST_VISIT_AT
            );
            const sessionId = Storage.get(
                KSTELO.STORAGE_KEYS.SESSION_ID
            );

            if (!lastVisitAt || !sessionId) {
                return true;
            }

            const lastVisitTimestamp = Date.parse(lastVisitAt);

            if (Number.isNaN(lastVisitTimestamp)) {
                return true;
            }

            return (
                Utils.nowTimestamp() - lastVisitTimestamp >
                KSTELO.SESSION_TIMEOUT_MS
            );
        },

        createSession() {
            const sessionId = Utils.generateId("ses");
            const currentCount = Utils.toInteger(
                Storage.get(KSTELO.STORAGE_KEYS.SESSION_COUNT),
                0
            );
            const newSessionCount = currentCount + 1;
            const sessionStartedAt = Utils.nowISO();

            Storage.set(KSTELO.STORAGE_KEYS.SESSION_ID, sessionId);
            Storage.set(
                KSTELO.STORAGE_KEYS.SESSION_COUNT,
                newSessionCount
            );
            Storage.set(
                KSTELO.STORAGE_KEYS.SESSION_STARTED_AT,
                sessionStartedAt
            );

            return {
                sessionId,
                sessionCount: newSessionCount,
                sessionStartedAt
            };
        },

        getCurrentSession() {
            if (this.isSessionExpired()) {
                return this.createSession();
            }

            return {
                sessionId: Storage.get(
                    KSTELO.STORAGE_KEYS.SESSION_ID
                ),
                sessionCount: Utils.toInteger(
                    Storage.get(KSTELO.STORAGE_KEYS.SESSION_COUNT),
                    1
                ),
                sessionStartedAt: Storage.get(
                    KSTELO.STORAGE_KEYS.SESSION_STARTED_AT
                )
            };
        },

        initialize() {
            const visitorId = this.getVisitorId();
            const firstVisitAt = this.getFirstVisitAt();
            const currentSession = this.getCurrentSession();
            const lastVisitAt = Utils.nowISO();

            Storage.set(
                KSTELO.STORAGE_KEYS.LAST_VISIT_AT,
                lastVisitAt
            );

            this.data = {
                visitorId,
                sessionId: currentSession.sessionId,
                firstVisitAt,
                lastVisitAt,
                sessionCount: currentSession.sessionCount,
                sessionStartedAt: currentSession.sessionStartedAt
            };

            return this.data;
        },

        refreshLastVisit() {
            if (!this.data) {
                return;
            }

            const lastVisitAt = Utils.nowISO();

            Storage.set(
                KSTELO.STORAGE_KEYS.LAST_VISIT_AT,
                lastVisitAt
            );
            this.data.lastVisitAt = lastVisitAt;
        }
    };

    const EasySell = {
        retryCount: 0,
        maxRetries: 20,
        retryDelayMs: 500,
        observer: null,

        getField(fieldName) {
            const directSelectors = [
                `[name="${fieldName}"]`,
                `#${fieldName}`,
                `[data-name="${fieldName}"]`
            ];

            for (const selector of directSelectors) {
                const element = document.querySelector(selector);

                if (element) {
                    return element;
                }
            }

            const groups = document.querySelectorAll(
                "#es-form .es-group, .es-form .es-group"
            );

            for (const group of groups) {
                const label = group.querySelector("label");

                if (
                    label &&
                    label.textContent.trim() === fieldName
                ) {
                    return group.querySelector(
                        "input, textarea, select"
                    );
                }
            }

            return null;
        },

        setFieldValue(fieldName, value) {
            const field = this.getField(fieldName);

            if (!field) {
                return false;
            }

            const normalizedValue =
                value === null || value === undefined
                    ? ""
                    : String(value);

            if (field.value !== normalizedValue) {
                field.value = normalizedValue;
                field.setAttribute("value", normalizedValue);
                Utils.dispatchInputEvents(field);
            }

            return true;
        },

        fill(data) {
            if (!data) {
                return false;
            }

            const mappings = [
                ["visitorId", KSTELO.EASELL_FIELDS.visitorId],
                ["sessionId", KSTELO.EASELL_FIELDS.sessionId],
                ["firstVisitAt", KSTELO.EASELL_FIELDS.firstVisitAt],
                ["lastVisitAt", KSTELO.EASELL_FIELDS.lastVisitAt],
                ["sessionCount", KSTELO.EASELL_FIELDS.sessionCount],

                ["attributionId", KSTELO.EASELL_FIELDS.attributionId],
                ["utmSource", KSTELO.EASELL_FIELDS.utmSource],
                ["utmMedium", KSTELO.EASELL_FIELDS.utmMedium],
                ["utmCampaign", KSTELO.EASELL_FIELDS.utmCampaign],
                ["utmContent", KSTELO.EASELL_FIELDS.utmContent],
                ["utmTerm", KSTELO.EASELL_FIELDS.utmTerm],
                ["fbclid", KSTELO.EASELL_FIELDS.fbclid],
                ["campaignId", KSTELO.EASELL_FIELDS.campaignId],
                ["adsetId", KSTELO.EASELL_FIELDS.adsetId],
                ["adId", KSTELO.EASELL_FIELDS.adId],
                ["landingPage", KSTELO.EASELL_FIELDS.landingPage],
                ["referrer", KSTELO.EASELL_FIELDS.referrer]
            ];

            const results = mappings.map(([dataKey, fieldName]) =>
                this.setFieldValue(fieldName, data[dataKey])
            );

            return results.every(Boolean);
        },

        retry(data) {
            const completed = this.fill(data);

            if (completed) {
                return;
            }

            if (this.retryCount >= this.maxRetries) {
                console.warn(
                    "[Kstelo] No se encontraron todos los campos de EasySell."
                );
                return;
            }

            this.retryCount += 1;

            window.setTimeout(() => {
                this.retry(data);
            }, this.retryDelayMs);
        },

        observe(data) {
            if (
                typeof MutationObserver === "undefined" ||
                !document.body
            ) {
                return;
            }

            if (this.observer) {
                this.observer.disconnect();
            }

            this.observer = new MutationObserver(() => {
                this.fill(data);
            });

            this.observer.observe(document.body, {
                childList: true,
                subtree: true
            });
        },

        initialize(data) {
            this.retryCount = 0;
            this.fill(data);
            this.retry(data);
            this.observe(data);
        }
    };

    const KsteloTracking = {
        version: KSTELO.VERSION,
        data: null,

        buildData() {
            return {
                ...Session.data,
                ...Attribution.data
            };
        },

        init() {
            try {
                Session.initialize();
                Attribution.initialize();

                this.data = this.buildData();

                EasySell.initialize(this.data);

                console.info(
                    `[Kstelo] Tracking iniciado. Versión ${this.version}`,
                    this.data
                );

                return this.getData();
            } catch (error) {
                console.error(
                    "[Kstelo] Error al iniciar el tracking.",
                    error
                );
                return null;
            }
        },

        getData() {
            return this.data ? { ...this.data } : null;
        },

        syncEasySell() {
            if (!this.data) {
                return false;
            }

            Session.refreshLastVisit();
            Attribution.initialize();

            this.data = this.buildData();

            return EasySell.fill(this.data);
        },

        resetSession() {
            Storage.remove(KSTELO.STORAGE_KEYS.SESSION_ID);
            Storage.remove(KSTELO.STORAGE_KEYS.SESSION_STARTED_AT);

            Session.initialize();
            Attribution.initialize();

            this.data = this.buildData();
            EasySell.initialize(this.data);

            return this.getData();
        },

        resetAttribution() {
            [
                KSTELO.STORAGE_KEYS.ATTRIBUTION_ID,
                KSTELO.STORAGE_KEYS.UTM_SOURCE,
                KSTELO.STORAGE_KEYS.UTM_MEDIUM,
                KSTELO.STORAGE_KEYS.UTM_CAMPAIGN,
                KSTELO.STORAGE_KEYS.UTM_CONTENT,
                KSTELO.STORAGE_KEYS.UTM_TERM,
                KSTELO.STORAGE_KEYS.FBCLID,
                KSTELO.STORAGE_KEYS.CAMPAIGN_ID,
                KSTELO.STORAGE_KEYS.ADSET_ID,
                KSTELO.STORAGE_KEYS.AD_ID,
                KSTELO.STORAGE_KEYS.LANDING_PAGE,
                KSTELO.STORAGE_KEYS.REFERRER
            ].forEach((key) => Storage.remove(key));

            Attribution.initialize();
            this.data = this.buildData();
            EasySell.initialize(this.data);

            return this.getData();
        },

        resetAll() {
            Object.values(KSTELO.STORAGE_KEYS).forEach(
                (key) => Storage.remove(key)
            );

            Session.initialize();
            Attribution.initialize();

            this.data = this.buildData();
            EasySell.initialize(this.data);

            return this.getData();
        }
    };

    window.KsteloTracking = KsteloTracking;

    function start() {
        KsteloTracking.init();
    }

    if (document.readyState === "loading") {
        document.addEventListener(
            "DOMContentLoaded",
            start,
            { once: true }
        );
    } else {
        start();
    }
})(window, document);